import { DecryptedForm, DecryptedPatient, Patient } from '@icure/cardinal-sdk/model.mjs'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { cardinalApi, CardinalApiState, guard } from '../services/auth.api'
import { loadFromIterator, tagById, tagsByIds } from './utils'
import { AccessLevel, FormApi } from '@icure/cardinal-sdk'
import { FormFilters } from '@icure/cardinal-sdk/filters.mjs'

export async function loadChildrenRecursively(formApi: FormApi, hcPartyId: string, id: string): Promise<DecryptedForm[]> {
  const acc: DecryptedForm[] = []
  const newForms = await loadFromIterator(await formApi.filterFormsBy(FormFilters.byParentIdForDataOwner(hcPartyId, id)), 1000)
  acc.push(...newForms)
  await Promise.all(newForms.map(async (form) => acc.push(...(await loadChildrenRecursively(formApi, hcPartyId, form.id)))))
  return acc
}

export const formApiRtk = createApi({
  reducerPath: 'formApi',
  tagTypes: ['Form'],
  baseQuery: fetchBaseQuery({
    baseUrl: '',
  }),
  endpoints: (builder) => ({
    createForm: builder.mutation<DecryptedForm | undefined, { patient: DecryptedPatient; form: DecryptedForm }>({
      async queryFn({ patient, form }, { getState }) {
        const {
          cardinalApi: { user },
        } = getState() as { cardinalApi: CardinalApiState }

        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi, user], async ([formApi, user]): Promise<DecryptedForm | undefined> => {
          const formWithMetadata = await formApi.withEncryptionMetadata(form, patient, {
            user,
            delegates: Object.fromEntries(
              Object.values(patient.securityMetadata?.secureDelegations ?? [])
                .map((d) => (d.delegate ? [d.delegate, AccessLevel.Write] : undefined))
                .filter((x) => !!x),
            ),
          })
          if (!formWithMetadata) {
            throw new Error('Error while creating FormWithMetadata')
          }
          return await formApi.createForm(formWithMetadata)
        })
      },

      invalidatesTags: (result) =>
        !!result
          ? [
              { type: 'Form', id: 'all' },
              { type: 'Form', id: result.id },
            ]
          : [],
    }),
    modifyForm: builder.mutation<DecryptedForm | undefined, DecryptedForm>({
      async queryFn(form, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<DecryptedForm | undefined> => {
          return await formApi.modifyForm(form)
        })
      },

      invalidatesTags: (result) =>
        !!result
          ? [
              { type: 'Form', id: 'all' },
              { type: 'Form', id: result.id },
            ]
          : [],
    }),
    deleteForm: builder.mutation<void | undefined, DecryptedForm>({
      async queryFn(form, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<void> => {
          await formApi.deleteForm(form)
        })
      },

      invalidatesTags: (result, _error, form) => [
        { type: 'Form', id: 'all' },
        { type: 'Form', id: form.id },
      ],
    }),
    findFormsByHcPartyPatient: builder.query<DecryptedForm[] | undefined, { hcPartyId: string; patient: Patient }>({
      async queryFn({ hcPartyId, patient }, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<DecryptedForm[]> => {
          return await loadFromIterator(await formApi.filterFormsBySorted(FormFilters.byPatientsOpeningDateForDataOwner(hcPartyId, [patient])), 1000)
        })
      },
      providesTags: tagsByIds('Form', 'all'),
    }),
    getChildrenOfFormWithId: builder.query<DecryptedForm[] | undefined, { hcPartyId: string; id: string }>({
      async queryFn({ hcPartyId, id }, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<DecryptedForm[]> => {
          return await loadFromIterator(await formApi.filterFormsBy(FormFilters.byParentIdForDataOwner(hcPartyId, id)), 1000)
        })
      },
      providesTags: tagsByIds('Form', 'all'),
    }),
    getAllChildrenOfFormWithId: builder.query<DecryptedForm[] | undefined, { hcPartyId: string; id: string }>({
      async queryFn({ hcPartyId, id }, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<DecryptedForm[]> => {
          return await loadChildrenRecursively(formApi, hcPartyId, id)
        })
      },
      providesTags: tagsByIds('Form', 'all'),
    }),

    getFormWithId: builder.query<DecryptedForm | undefined, { id: string }>({
      async queryFn({ id }, { getState }) {
        const formApi = (await cardinalApi(getState))?.form
        return guard([formApi], async ([formApi]): Promise<DecryptedForm | undefined> => {
          return await formApi.getForm(id)
        })
      },
      providesTags: tagById('Form'),
    }),
  }),
})

export const { useCreateFormMutation, useModifyFormMutation, useDeleteFormMutation, useFindFormsByHcPartyPatientQuery, useGetFormWithIdQuery, useGetAllChildrenOfFormWithIdQuery } =
  formApiRtk
